import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { imobiliariaDataset } from '../../shared/data/imobiliariaData';
import { Building, Plus, Search, Users, Home, ShieldCheck, CheckCircle2, FileText } from 'lucide-react';

export const RealEstateSystem: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  const [activeTab, setActiveTab] = useState<'cadastro' | 'crm' | 'imoveis'>('cadastro');
  const [createdSuccess, setCreatedSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans flex flex-col selection:bg-[#1E3A8A] selection:text-white">
      
      {/* Header Neutto Style ERP - STRICT NO SHADOWS */}
      <header className="h-20 bg-white border-b border-slate-200 px-8 flex items-center justify-between shrink-0">
         <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-2xl bg-[#1E3A8A] border border-blue-900 text-white flex items-center justify-center font-black">
               <Building className="w-5 h-5" />
            </div>
            <div>
               <h1 className="font-heading font-black text-lg text-[#0F172A] tracking-wider uppercase">Neutto CRM — {prospect.trade_name}</h1>
               <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Gestão Imobiliária & Captação Inteligente</p>
            </div>
         </div>

         <div className="flex items-center gap-3">
            <button 
               onClick={() => setActiveTab('cadastro')}
               className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors border ${activeTab === 'cadastro' ? 'bg-[#1E3A8A] text-white border-blue-900' : 'bg-slate-100 text-slate-700 border-slate-200'}`}
            >
               + Cadastrar Imóvel
            </button>
            <button 
               onClick={() => setActiveTab('crm')}
               className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors border ${activeTab === 'crm' ? 'bg-[#1E3A8A] text-white border-blue-900' : 'bg-slate-100 text-slate-700 border-slate-200'}`}
            >
               CRM Clientes
            </button>
         </div>
      </header>

      <main className="flex-1 p-8 max-w-6xl mx-auto w-full">
         
         {/* TAB 1: CADASTRO SIMPLIFICADO DE IMÓVEL (NEUTTO STYLE) */}
         {activeTab === 'cadastro' && (
            <div className="bg-white p-8 rounded-2xl border border-slate-200 space-y-6">
               <div className="border-b border-slate-200 pb-4">
                  <span className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest">Neutto Engine</span>
                  <h2 className="text-2xl font-heading font-black text-[#0F172A]">Nunca foi tão simples cadastrar um imóvel.</h2>
                  <p className="text-xs text-slate-500 mt-1">Cadastro visual, rápido e inteligente. Fotos, infraestrutura e destaques em segundos, adeus aos sistemas lentos do passado.</p>
               </div>

               {createdSuccess ? (
                  <div className="bg-emerald-50 border border-emerald-200 p-8 rounded-2xl text-center space-y-3">
                     <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                     <h3 className="text-xl font-bold text-emerald-900">Imóvel Cadastrado e Publicado com Sucesso!</h3>
                     <p className="text-xs text-emerald-700">O imóvel já está visível na vitrine web da imobiliária e sincronizado no CRM.</p>
                     <button onClick={() => setCreatedSuccess(false)} className="bg-[#1E3A8A] text-white px-6 py-3 rounded-xl font-bold text-xs uppercase">Cadastrar Outro Imóvel</button>
                  </div>
               ) : (
                  <form onSubmit={(e) => { e.preventDefault(); setCreatedSuccess(true); }} className="space-y-6">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                           <label className="text-xs font-bold text-slate-700 block mb-1">Título do Imóvel</label>
                           <input type="text" placeholder="Ex: Apartamento 58m² no Centro" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none" />
                        </div>
                        <div>
                           <label className="text-xs font-bold text-slate-700 block mb-1">Tipo de Imóvel</label>
                           <select className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none">
                              <option>Apartamento</option>
                              <option>Casa</option>
                              <option>Cobertura</option>
                              <option>Sala Comercial</option>
                           </select>
                        </div>
                     </div>

                     <div className="grid grid-cols-3 gap-4">
                        <div>
                           <label className="text-xs font-bold text-slate-700 block mb-1">Valor do Aluguel (R$)</label>
                           <input type="number" placeholder="1800" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none" />
                        </div>
                        <div>
                           <label className="text-xs font-bold text-slate-700 block mb-1">Condomínio (R$)</label>
                           <input type="number" placeholder="320" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none" />
                        </div>
                        <div>
                           <label className="text-xs font-bold text-slate-700 block mb-1">Área Útil (m²)</label>
                           <input type="number" placeholder="60" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none" />
                        </div>
                     </div>

                     <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Endereço Completo & Bairro em Chapecó/SC</label>
                        <input type="text" placeholder="Rua Pernambuco, 726-D - Bairro Santo Antônio" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none" />
                     </div>

                     <button type="submit" className="w-full bg-[#1E3A8A] text-white py-4 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-900 transition-colors">
                        Salvar e Publicar Imóvel na Vitrine
                     </button>
                  </form>
               )}
            </div>
         )}

         {/* TAB 2: CRM DE CLIENTES (NEUTTO STYLE) */}
         {activeTab === 'crm' && (
            <div className="bg-white p-8 rounded-2xl border border-slate-200 space-y-6">
               <div className="border-b border-slate-200 pb-4">
                  <span className="text-[10px] text-blue-600 font-bold uppercase tracking-widest">CRM Centralizado</span>
                  <h2 className="text-2xl font-heading font-black text-[#0F172A]">Clientes organizados como nunca.</h2>
                  <p className="text-xs text-slate-500 mt-1">Centralize CPFs/CNPJs, contatos ilimitados e todo o histórico em uma única interface. É o fim das anotações soltas e planilhas quebradas.</p>
               </div>

               <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                     <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider">
                        <tr>
                           <th className="p-3">Cliente / CPF</th>
                           <th className="p-3">Interesse</th>
                           <th className="p-3">Orçamento</th>
                           <th className="p-3">Status Lead</th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-200">
                        <tr className="hover:bg-slate-50">
                           <td className="p-3 font-bold text-[#0F172A]">Renato Nogueira<span className="block text-[10px] text-slate-400">CPF: 028.910.849-00</span></td>
                           <td className="p-3 text-slate-600">Apartamento 2q no Centro</td>
                           <td className="p-3 font-bold text-emerald-600">Até R$ 2.200/mês</td>
                           <td className="p-3"><span className="bg-blue-50 text-blue-800 text-[10px] font-bold px-2 py-0.5 rounded border border-blue-200">Visita Agendada</span></td>
                        </tr>
                        <tr className="hover:bg-slate-50">
                           <td className="p-3 font-bold text-[#0F172A]">Viviane Silva<span className="block text-[10px] text-slate-400">CPF: 118.490.129-33</span></td>
                           <td className="p-3 text-slate-600">Lançamento Ámbar (Macodesc)</td>
                           <td className="p-3 font-bold text-emerald-600">R$ 950.000,00</td>
                           <td className="p-3"><span className="bg-amber-50 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded border border-amber-200">Proposta Enviada</span></td>
                        </tr>
                     </tbody>
                  </table>
               </div>
            </div>
         )}

      </main>

    </div>
  );
};
